import { ExpandLess, ExpandMore, InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFormikContext } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormControls, FormikNumericField } from 'components';
import { useUserContext, useUserContextUtils } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { P2PProviderDto } from 'types';

type Props = {
  configPath: 'payinConfig' | 'payoutConfig';
  fiatCurrencyCode?: string;
};

type FormValues = P2PProviderDto;

export const PaymentTypeAmountLimits: React.FC<Props> = ({
  configPath,
  fiatCurrencyCode,
}) => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.p2p_providers.fields',
  });
  const { values } = useFormikContext<FormValues>();
  const { paymentTypes, allTradeMethods } = useUserContext();
  const { getPaymentTypeLabel } = useUserContextUtils();
  const [expanded, setExpanded] = useState(false);

  const selectedTradeMethodIds = useMemo(
    () =>
      values[configPath]?.tradeMethods?.map((tradeMethod) => tradeMethod.id) ||
      [],
    [values, configPath],
  );

  const uniquePaymentTypeIds = useMemo(() => {
    if (!selectedTradeMethodIds.length || !allTradeMethods?.length) {
      return new Set<string>();
    }

    const paymentTypeIds = new Set<string>();
    allTradeMethods
      .filter((tradeMethod) => selectedTradeMethodIds.includes(tradeMethod.id))
      .forEach((method) => {
        if (method.paymentTypeId) {
          paymentTypeIds.add(method.paymentTypeId);
        }
      });

    return paymentTypeIds;
  }, [selectedTradeMethodIds, allTradeMethods]);

  const selectedPaymentTypes = useMemo(() => {
    if (!uniquePaymentTypeIds.size || !paymentTypes?.length) {
      return [];
    }

    return paymentTypes
      .filter((paymentType) => uniquePaymentTypeIds.has(paymentType.id))
      .map((paymentType) => ({
        id: paymentType.id,
        name: getPaymentTypeLabel({ id: paymentType.id }),
      }));
  }, [uniquePaymentTypeIds, paymentTypes, getPaymentTypeLabel]);

  const globalMin = values[configPath]?.orderMinAmount || 0;
  const globalMax = values[configPath]?.orderMaxAmount || 0;

  if (!selectedPaymentTypes.length) {
    return null;
  }

  return (
    <>
      <Divider>
        <Box display="flex" alignItems="center">
          <Typography variant="body2" sx={{ mr: 1 }}>
            {t('payment_type_specific_limits')}
          </Typography>
          <Tooltip
            title={t('payment_type_specific_limits_description')}
            placement="top"
          >
            <InfoOutlined
              sx={{ fontSize: 'small', color: 'text.secondary', mr: 1 }}
            />
          </Tooltip>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ p: 0.5 }}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Divider>

      <Collapse in={expanded}>
        <Box sx={{ mt: 2 }}>
          {selectedPaymentTypes.map((paymentType) => (
            <Box key={paymentType.id}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, mt: 2, fontWeight: 600 }}
              >
                {paymentType.name}
              </Typography>
              <FormControls row>
                <FormikNumericField
                  name={`${configPath}.paymentTypeAmountLimits.${paymentType.id}.minAmount`}
                  label={tCommon('common.min')}
                  fullWidth
                  suffix={fiatCurrencyCode}
                  allowNegative={false}
                  placeholder={globalMin ? String(globalMin) : '0'}
                />
                <FormikNumericField
                  name={`${configPath}.paymentTypeAmountLimits.${paymentType.id}.maxAmount`}
                  label={tCommon('common.max')}
                  fullWidth
                  suffix={fiatCurrencyCode}
                  allowNegative={false}
                  placeholder={globalMax ? String(globalMax) : '0'}
                />
              </FormControls>
            </Box>
          ))}
        </Box>
      </Collapse>
    </>
  );
};
